import { Injectable } from '@angular/core';
import { tap } from 'rxjs/operators';
import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { setParams } from '@app-core/models/core.model';
import { API } from '@app-core/constants/api.constants';
import { HttpCacheService } from '@app-core/services/cache/cache.service';
import { HttpClient } from '@angular/common/http';
import {
  DataBusEvent,
  Geofence,
  GeofenceFilter,
  GeofenceSetting,
  GeofenceToast,
  ListGeofenceActivityQueryParams,
  ListGeofenceQueryParams,
  GeofenceActivityListResponse,
} from '@app-asset-config/models/geofence.model';
import { AccessService } from '@app-core/services/access/access.service';
import { MAPBOX_ACCESS_TOKEN } from '@app-request-video/constants/request-video.constants';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class GeofenceService {
  private _navigator: Subject<any> = new Subject();

  // geofence listing related variables
  private geofenceList = [];
  geofenceFilter: GeofenceFilter = { search: '', propertyIds: [], tagIds: [] };
  private geofenceLimit: number = 50;
  private geofenceSkip: number = 0;
  private totalGeofenceCount: number = 0;
  geofenceSubject = new Subject<Geofence[]>();
  geofenceFilterApplied = new Subject<boolean>();

  // geofence setting related variables
  private geofenceSettingList: GeofenceSetting[] = [];
  private geofenceSettingLimit: number = 50;
  private geofenceSettingSkip = 0;
  private totalGeofenceSettingCount = 0;
  geofenceSettingSubject = new Subject<GeofenceSetting[]>();
  geofenceDefaultSettingSubject = new BehaviorSubject<GeofenceSetting>(null);

  commonDataBus = new Subject<{ event: DataBusEvent; data?: any }>();

  // toaster related variables
  private toastsSubject = new BehaviorSubject<GeofenceToast[]>([]);
  toasts$ = this.toastsSubject.asObservable();
  private toastsCounter = 0;

  // skeleton loader subject
  geofenceSkeletonLoaderSubject = new BehaviorSubject<boolean>(false);

  DEFAULT_SETTING_COLOR = '#00A6F4';

  constructor(
    private http: HttpClient,
    private cacheService: HttpCacheService,
    private accessService: AccessService,
    private translate: TranslateService
  ) {}

  get navigator() {
    return this._navigator;
  }

  navigateTo(componentRef: any, data: any = {}) {
    this._navigator.next({ componentRef, options: { type: 'push', data } });
  }

  navigateToWithoutHide(componentRef: any, data: any = {}) {
    this._navigator.next({ componentRef, options: { type: 'push', data, doNotHide: true } });
  }

  pop(data: any = {}) {
    this._navigator.next({ options: { type: 'pop', data } });
  }

  createGeofence(body: any) {
    const url = API.CREATE_GEOFENCE;
    return this.http.post(url, body).pipe(
      tap(() => {
        this.cacheService.burstCache$.next(API.LIST_GEOFENCE);
      })
    );
  }

  updateGeofence(geofenceId: number, body: any) {
    const url = API.UPDATE_GEOFENCE(geofenceId);

    // adding userInfo for update calls
    const { postgresId: id, userId, name, loginName: email } = this.accessService.getLoginInfo();
    body['userInfo'] = { id, userId, name, email };

    return this.http.patch(url, body).pipe(
      tap(() => {
        this.cacheService.burstCache$.next(API.LIST_GEOFENCE);
      })
    );
  }

  listGeofence(params: ListGeofenceQueryParams, isRefresh: boolean = false): any {
    const httpOptions = {
      params: setParams(params),
    };

    if (isRefresh) this.cacheService.burstCache$.next(API.LIST_GEOFENCE);

    return this.http.get(API.LIST_GEOFENCE, httpOptions);
  }

  deleteGeofence(geofenceId: number) {
    const url = API.DELETE_GEOFENCE(geofenceId);
    return this.http.delete(url).pipe(
      tap(() => {
        this.cacheService.burstCache$.next(API.LIST_GEOFENCE);
      })
    );
  }

  createGeofenceSetting(body: any) {
    const url = API.CREATE_GEOFENCE_SETTING;
    return this.http.post(url, body).pipe(
      tap(() => {
        this.cacheService.burstCache$.next(API.LIST_GEOFENCE_SETTING);
      })
    );
  }

  updateGeofenceSetting(settingId: number, body: any) {
    const url = API.UPDATE_GEOFENCE_SETTING(settingId);
    return this.http.patch(url, body).pipe(
      tap(() => {
        this.cacheService.burstCache$.next(API.LIST_GEOFENCE_SETTING);
      })
    );
  }

  listGeofenceSetting(params: { skip: number; limit: number; propertyId?: number }, isRefresh: boolean = false) {
    const httpOptions = {
      params: setParams(params),
    };

    if (isRefresh) this.cacheService.burstCache$.next(API.LIST_GEOFENCE_SETTING);

    return this.http.get(API.LIST_GEOFENCE_SETTING, httpOptions);
  }

  deleteGeofenceSetting(settingId: number) {
    const url = API.DELETE_GEOFENCE_SETTING(settingId);
    return this.http.delete(url).pipe(
      tap(() => {
        this.cacheService.burstCache$.next(API.LIST_GEOFENCE_SETTING);
      })
    );
  }

  /**
   * Function to initiate api call to fetch the geofence
   * It is palced here cause required in two places
   * @param filter
   * @param freshRequest
   * @returns void
   */
  fetchGeofence(freshRequest: boolean = false) {
    if (this.geofenceSkip >= this.totalGeofenceCount && !freshRequest) {
      return;
    }

    this.geofenceSkeletonLoaderSubject.next(true);

    let isFilterApplied = false;

    if (freshRequest) this.geofenceSkip = 0;

    const params = new ListGeofenceQueryParams({
      limit: this.geofenceLimit,
      skip: this.geofenceSkip,
      search: this.geofenceFilter.search,
      propertyIds: this.geofenceFilter.propertyIds,
      tagIds: this.geofenceFilter.tagIds,
    });

    if (params.propertyIds) {
      isFilterApplied = true;
      params['propertyIds[]'] = [...params.propertyIds];
      delete params.propertyIds;
    }

    if (params.tagIds) {
      isFilterApplied = true;
      params['tagIds[]'] = [...params.tagIds];
      delete params.tagIds;
    }

    return this.listGeofence(params).subscribe(
      (res: any) => {
        if (freshRequest) {
          // doing this because on load this function will be triggered twice because of permission directive on mat-tab
          this.geofenceSkip = 0;

          this.geofenceList = [...res.rows];
          this.totalGeofenceCount = res.totalCount;

          if (isFilterApplied) {
            this.geofenceFilterApplied.next(true);
          } else {
            this.geofenceFilterApplied.next(false);
          }

          if (!this.geofenceList.length && !isFilterApplied && !this.geofenceFilter.search) {
            this.navigateTo('' as any, { landingPage: true });
          }
        } else {
          this.geofenceList = [...this.geofenceList, ...res.rows];
        }

        this.geofenceSkip += res.rows.length;

        this.geofenceSubject.next([...this.geofenceList]);
        this.geofenceSkeletonLoaderSubject.next(false);
      },
      (err) => {
        console.log('Listing geofence error : ', err);
        this.geofenceSkeletonLoaderSubject.next(false);
      }
    );
  }

  applyGeofenceFilter(filter: GeofenceFilter) {
    this.geofenceFilter = { ...this.geofenceFilter, ...filter };
    this.fetchGeofence(true);
  }

  /**
   *  First call will be from geofence-layout.ts - to get the default property value
   *
   * Function to initiate api call to fetch the geofence setting
   * It is palced here cause required in two places
   * @param freshRequest
   * @returns void
   */
  fetchGeofenceSetting(freshRequest: boolean = false) {
    if (this.geofenceSettingSkip >= this.totalGeofenceSettingCount && !freshRequest) {
      return;
    }

    this.geofenceSkeletonLoaderSubject.next(true);

    if (freshRequest) this.geofenceSettingSkip = 0;

    const params = {
      limit: this.geofenceSettingLimit,
      skip: this.geofenceSettingSkip,
    };

    this.listGeofenceSetting(params).subscribe(
      (res: any) => {
        if (freshRequest) this.geofenceSettingSkip = 0;

        this.geofenceSettingSkip += res.rows.length;

        // passing default property
        res.defaultProperty['isDefaultProperty'] = true;
        if (!res.defaultProperty.colourHex) {
          res.defaultProperty.colourHex = this.DEFAULT_SETTING_COLOR;
        }
        this.geofenceDefaultSettingSubject.next(res.defaultProperty);

        if (freshRequest) {
          this.geofenceSettingList = [...res.rows];
          this.totalGeofenceSettingCount = res.totalCount;
        } else {
          this.geofenceSettingList = [...this.geofenceSettingList, ...res.rows];
        }

        this.geofenceSettingSubject.next(this.geofenceSettingList);
        this.geofenceSkeletonLoaderSubject.next(false);
      },
      (err) => {
        this.geofenceSkeletonLoaderSubject.next(false);
        this.showToasts(this.translate.instant('gefenceToastGeofenceListFailed'), 'error');
        console.log('Listing geofence setting error : ', err);
      }
    );
  }

  listGeofenceActivity(params: ListGeofenceActivityQueryParams, isRefresh: boolean = false): Observable<GeofenceActivityListResponse> {
    const httpOptions = {
      params: setParams(params),
    };

    if (isRefresh) this.cacheService.burstCache$.next(API.LIST_GEOFENCE_ACTIVITY);

    return this.http.get<GeofenceActivityListResponse>(API.LIST_GEOFENCE_ACTIVITY, httpOptions);
  }

  listGeofenceLocation(params: ListGeofenceActivityQueryParams, isRefresh: boolean = false) {
    const httpOptions = {
      params: setParams(params),
    };

    if (isRefresh) this.cacheService.burstCache$.next(API.LIST_GEOFENCE_LOCATION);

    return this.http.get(API.LIST_GEOFENCE_LOCATION, httpOptions);
  }

  getLatLngFromAddress(params: { q: string; limit: number }) {
    params['access_token'] = MAPBOX_ACCESS_TOKEN;
    const httpOptions = { params: setParams(params) };
    return this.http.get(API.GET_LATLNG_FROM_ADDRESS(), httpOptions);
  }

  getAddressFromLatLng(params: { longitude: number; latitude: number }) {
    // for reverse geocoding limit is always 1
    params['limit'] = 1;

    params['access_token'] = MAPBOX_ACCESS_TOKEN;

    const httpOptions = { params: setParams(params) };

    return this.http.get(API.GET_ADDRESS_FROM_LATLNG(), httpOptions);
  }

  /** Toaster related functionality */
  showToasts(message: string, type: GeofenceToast['type'] = 'success', duration = 3000) {
    const newToast: GeofenceToast = {
      id: ++this.toastsCounter,
      message,
      type,
      duration,
    };

    const current = this.toastsSubject.value;
    this.toastsSubject.next([...current, newToast]);

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => this.removeToasts(newToast.id), duration);
    }
  }

  private removeToasts(id: number) {
    const updated = this.toastsSubject.value.filter((t) => t.id !== id);
    this.toastsSubject.next(updated);
  }
  /** END - Toaster related functionality */

  // function to calculate coordinates for circle, given a point and radius
  /** Logic and code from chatgpt */
  calculateCoordinatesForCircle(lng: number, lat: number, radius: number, points = 72) {
    // converting to number if lat lng in string format
    lng = typeof lng === 'string' ? parseFloat(lng) : lng;
    lat = typeof lat === 'string' ? parseFloat(lat) : lat;

    const coords = [];
    const earthRadius = 6371000; // meters

    for (let i = 0; i < points; i++) {
      const angle = (i / points) * 2 * Math.PI;

      // Offset in radians
      const latOffset = (radius / earthRadius) * Math.cos(angle);
      const lngOffset = ((radius / earthRadius) * Math.sin(angle)) / Math.cos((lat * Math.PI) / 180);

      // Convert offsets to degrees
      const pointLat = lat + (latOffset * 180) / Math.PI;
      const pointLng = lng + (lngOffset * 180) / Math.PI;

      coords.push([pointLng, pointLat]);
    }

    coords.push([coords[0][0], coords[0][1]]); // close the circle

    return coords;
  }

  calculateCoordinatesForSquare(lng: number, lat: number, sideMeters: number) {
    const earthRadius = 6371000; // meters
    const halfSide = sideMeters / 2;

    // Convert meters → degrees latitude
    const latOffset = (halfSide / earthRadius) * (180 / Math.PI);

    // Convert meters → degrees longitude
    const lngOffset = (halfSide / (earthRadius * Math.cos((lat * Math.PI) / 180))) * (180 / Math.PI);

    return [
      [lng - lngOffset, lat + latOffset], // top-left
      [lng + lngOffset, lat + latOffset], // top-right
      [lng + lngOffset, lat - latOffset], // bottom-right
      [lng - lngOffset, lat - latOffset], // bottom-left
      [lng - lngOffset, lat + latOffset], // top-left to close the square
    ];
  }
}
